import { i18n } from '../../../i18n/index.js';
// Человеческие описания событий аудита — общие для всех сущностей.
//
// До этого расшифровка жила только в задачах (`describeTaskActivity`), а карточка
// сделки показывала сырое значение `kind`. Теперь описание одно на всю систему.
import type { ActivityItem } from '../api/activities.client.js';

// Ключи — это имена полей записи, как их видит сервис: `contactId`, `dueAt`.
// Без подписи в историю попадало сырое `contactId`.
const FIELD_LABEL: Record<string, string> = {
  get title() {
    return i18n.t('crm.features.activity.field.title');
  },
  get name() {
    return i18n.t('crm.features.activity.field.title');
  },
  get description() {
    return i18n.t('crm.features.activity.field.description');
  },
  get body() {
    return i18n.t('crm.features.activity.field.body');
  },
  get priority() {
    return i18n.t('crm.features.activity.field.priority');
  },
  get status() {
    return i18n.t('crm.features.activity.field.status');
  },
  get assignee() {
    return i18n.t('crm.features.activity.field.assignee');
  },
  get assigneeMemberId() {
    return i18n.t('crm.features.activity.field.assignee');
  },
  get ownerMemberId() {
    return i18n.t('crm.features.activity.field.owner');
  },
  get deal() {
    return i18n.t('crm.features.activity.field.deal');
  },
  get dealId() {
    return i18n.t('crm.features.activity.field.deal');
  },
  get company() {
    return i18n.t('crm.features.activity.field.company');
  },
  get companyId() {
    return i18n.t('crm.features.activity.field.company');
  },
  get contact() {
    return i18n.t('crm.features.activity.field.contact');
  },
  get contactId() {
    return i18n.t('crm.features.activity.field.contact');
  },
  get task() {
    return i18n.t('crm.features.activity.field.task');
  },
  get taskId() {
    return i18n.t('crm.features.activity.field.task');
  },
  get dueAt() {
    return i18n.t('crm.features.activity.field.due_at');
  },
  get completedAt() {
    return i18n.t('crm.features.activity.field.completed_at');
  },
  get amount() {
    return i18n.t('crm.features.activity.field.amount');
  },
  get currency() {
    return i18n.t('crm.features.activity.field.currency');
  },
  get source() {
    return i18n.t('crm.features.activity.field.source');
  },
  get role() {
    return i18n.t('crm.features.activity.field.role');
  },
  email: 'email',
  get phone() {
    return i18n.t('crm.features.activity.field.phone');
  },
  get tags() {
    return i18n.t('crm.features.activity.field.tags');
  },
  get notes() {
    return i18n.t('crm.features.activity.field.notes');
  },
  get firstName() {
    return i18n.t('crm.features.activity.field.first_name');
  },
  get lastName() {
    return i18n.t('crm.features.activity.field.last_name');
  },
  get domain() {
    return i18n.t('crm.features.activity.field.domain');
  },
  get website() {
    return i18n.t('crm.features.activity.field.website');
  },
  get industry() {
    return i18n.t('crm.features.activity.field.industry');
  },
  get type() {
    return i18n.t('crm.features.activity.field.type');
  },
  get subject() {
    return i18n.t('crm.features.activity.field.subject');
  },
  get variables() {
    return i18n.t('crm.features.activity.field.variables');
  },
  get isActive() {
    return i18n.t('crm.features.activity.field.is_active');
  },
  get location() {
    return i18n.t('crm.features.activity.field.location');
  },
  get timezone() {
    return i18n.t('crm.features.activity.field.timezone');
  },
  get nextActivityDue() {
    return i18n.t('crm.features.activity.field.next_step');
  },
  get links() {
    return i18n.t('crm.features.activity.field.links');
  },
  get startsAt() {
    return i18n.t('crm.features.activity.field.starts_at');
  },
  get endsAt() {
    return i18n.t('crm.features.activity.field.ends_at');
  },
  get participants() {
    return i18n.t('crm.features.activity.field.participants');
  },
};

const TASK_STATUS_LABEL: Record<string, string> = {
  get todo() {
    return i18n.t('crm.features.activity.event.todo');
  },
  get in_progress() {
    return i18n.t('crm.features.activity.event.in_progress');
  },
  get done() {
    return i18n.t('crm.features.activity.event.done');
  },
  get cancelled() {
    return i18n.t('crm.features.activity.event.cancelled');
  },
};

/** Простые события «создано / удалено» — по одному слову на вид записи. */
const SIMPLE_LABEL: Record<string, string> = {
  get deal_created() {
    return i18n.t('crm.features.activity.event.deal_created');
  },
  get deal_deleted() {
    return i18n.t('crm.features.activity.event.deal_deleted');
  },
  get contact_created() {
    return i18n.t('crm.features.activity.event.contact_created');
  },
  get contact_deleted() {
    return i18n.t('crm.features.activity.event.contact_deleted');
  },
  get company_created() {
    return i18n.t('crm.features.activity.event.company_created');
  },
  get company_deleted() {
    return i18n.t('crm.features.activity.event.company_deleted');
  },
  get task_created() {
    return i18n.t('crm.features.activity.event.task_created');
  },
  get task_deleted() {
    return i18n.t('crm.features.activity.event.task_deleted');
  },
  get note_added() {
    return i18n.t('crm.features.activity.event.note_added');
  },
  get note_deleted() {
    return i18n.t('crm.features.activity.event.note_deleted');
  },
  get meeting_scheduled() {
    return i18n.t('crm.features.activity.event.meeting_scheduled');
  },
  get meeting_cancelled() {
    return i18n.t('crm.features.activity.event.meeting_cancelled');
  },
  get document_created() {
    return i18n.t('crm.features.activity.event.document_created');
  },
  get document_deleted() {
    return i18n.t('crm.features.activity.event.document_deleted');
  },
  get template_created() {
    return i18n.t('crm.features.activity.event.template_created');
  },
  get template_deleted() {
    return i18n.t('crm.features.activity.event.template_deleted');
  },
  get interaction_logged() {
    return i18n.t('crm.features.activity.event.interaction_logged');
  },
  get email_sent() {
    return i18n.t('crm.features.activity.event.email_sent');
  },
  get email_opened() {
    return i18n.t('crm.features.activity.event.email_opened');
  },
  get email_clicked() {
    return i18n.t('crm.features.activity.event.email_clicked');
  },
  get call_made() {
    return i18n.t('crm.features.activity.event.call_made');
  },
  get ai_tool_invoked() {
    return i18n.t('crm.features.activity.event.ai_tool_invoked');
  },
  get scenario_started() {
    return i18n.t('crm.features.activity.event.scenario_started');
  },
  get scenario_completed() {
    return i18n.t('crm.features.activity.event.scenario_completed');
  },
};

const UPDATE_LABEL: Record<string, string> = {
  get deal_updated() {
    return i18n.t('crm.features.activity.event.deal_updated');
  },
  get contact_updated() {
    return i18n.t('crm.features.activity.event.contact_updated');
  },
  get company_updated() {
    return i18n.t('crm.features.activity.event.company_updated');
  },
  get task_updated() {
    return i18n.t('crm.features.activity.event.task_updated');
  },
  get note_updated() {
    return i18n.t('crm.features.activity.event.note_updated');
  },
  get meeting_updated() {
    return i18n.t('crm.features.activity.event.meeting_updated');
  },
  get document_updated() {
    return i18n.t('crm.features.activity.event.document_updated');
  },
  get template_updated() {
    return i18n.t('crm.features.activity.event.template_updated');
  },
};

function changedFields(payload: Record<string, unknown>): string {
  const fields = Array.isArray(payload['fields']) ? (payload['fields'] as string[]) : [];
  return fields.map((field) => FIELD_LABEL[field] ?? field).join(', ');
}

export function describeActivity(activity: ActivityItem): string {
  const payload = activity.payload ?? {};

  // Привязки: в истории обеих сторон видно, что к чему привязали и что отвязали.
  if (activity.kind === 'entity_link_added' || activity.kind === 'entity_link_removed') {
    const target =
      typeof payload['target'] === 'string'
        ? payload['target']
        : i18n.t('crm.features.features.activity.lib.describeactivity.запись');
    return activity.kind === 'entity_link_added'
      ? i18n.t('crm.features.dynamic.features.activity.lib.describeactivity.1', { p0: target })
      : i18n.t('crm.features.dynamic.features.activity.lib.describeactivity.2', { p0: target });
  }

  // Наступивший срок — вычисляемое событие, его пишет не пользователь.
  if (activity.kind === 'deadline_passed') {
    return i18n.t('crm.features.features.activity.lib.describeactivity.срок_прошел');
  }

  // Напоминание о сроке задачи пишет воркер, а не пользователь: в subject
  // лежит название задачи.
  if (activity.kind === 'task_due_reminder') {
    const title = activity.subject?.trim();
    return title
      ? i18n.t('crm.features.activity.event.task_due_reminder', { p0: title })
      : i18n.t('crm.features.activity.event.task_due_reminder_plain');
  }

  if (activity.kind === 'task_status_changed') {
    const from = String(payload['from'] ?? '');
    const to = String(payload['to'] ?? '');
    return i18n.t('crm.features.dynamic.features.activity.lib.describeactivity.3', {
      p0: TASK_STATUS_LABEL[from] ?? from,
      p1: TASK_STATUS_LABEL[to] ?? to,
    });
  }

  if (activity.kind === 'deal_stage_changed') {
    return i18n.t('crm.features.dynamic.features.activity.lib.describeactivity.4', {
      p0: String(payload['from'] ?? '—'),
      p1: String(payload['to'] ?? '—'),
    });
  }

  const updateLabel = UPDATE_LABEL[activity.kind];
  if (updateLabel) {
    const fields = changedFields(payload);
    return fields
      ? i18n.t('crm.features.dynamic.features.activity.lib.describeactivity.5', { p0: fields })
      : updateLabel;
  }

  return SIMPLE_LABEL[activity.kind] ?? activity.kind;
}
